import { useState, useEffect } from "react";
import {
  MoreVertical,
  CalendarClock,
  PlusCircle,
  Paperclip,
  Share2,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { CATEGORIAS } from "@/lib/categorias";
import { setPrazoCte, addEventoManualCte, uploadComprovantesCte, gerarLinkRastreio } from "@/lib/ocorenApi";

const inputClass =
  "w-full px-3 py-2 text-sm border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#09090B] bg-white text-[#09090B]";
const labelClass =
  "block text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-semibold mb-1.5";

export const CteActions = ({ cte, onChanged, idx = 0 }) => {
  const [prazoOpen, setPrazoOpen] = useState(false);
  const [eventoOpen, setEventoOpen] = useState(false);
  const [comprovanteOpen, setComprovanteOpen] = useState(false);
  const [rastreioOpen, setRastreioOpen] = useState(false);
  const [rastreioLink, setRastreioLink] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);

  const obterLinkRastreio = async () => {
    setGeneratingLink(true);
    try {
      const res = await gerarLinkRastreio({
        tipo: "cte",
        cte_numero: cte.cte_numero,
        cnpj_transportadora: cte.cnpj_transportadora,
      });
      const link = `${window.location.origin}/rastreio/${res.token}`;
      setRastreioLink(link);
      setRastreioOpen(true);
    } catch {
      toast.error("Falha ao gerar link de rastreamento");
    } finally {
      setGeneratingLink(false);
    }
  };

  // Notas vinculadas
  const nfsDisponiveis = cte.nfs_numeros || [];
  const [selectedNfs, setSelectedNfs] = useState([]);

  // Reseta seleção de notas sempre que um modal abre
  const resetSelectedNfs = () => {
    setSelectedNfs(nfsDisponiveis);
  };

  const handleToggleNf = (nf) => {
    if (selectedNfs.includes(nf)) {
      setSelectedNfs(selectedNfs.filter((n) => n !== nf));
    } else {
      setSelectedNfs([...selectedNfs, nf]);
    }
  };

  const handleSelectAllNfs = () => {
    if (selectedNfs.length === nfsDisponiveis.length) {
      setSelectedNfs([]);
    } else {
      setSelectedNfs(nfsDisponiveis);
    }
  };

  // ---- Prazo state ----
  const [dataPrevista, setDataPrevista] = useState("");
  const [savingPrazo, setSavingPrazo] = useState(false);

  // ---- Evento manual state ----
  const [categoria, setCategoria] = useState("entregue");
  const [dataEvento, setDataEvento] = useState("");
  const [descricao, setDescricao] = useState("");
  const [codigo, setCodigo] = useState("");
  const [savingEvento, setSavingEvento] = useState(false);

  // ---- Comprovantes state ----
  const [files, setFiles] = useState([]);
  const [savingComprovante, setSavingComprovante] = useState(false);

  const openPrazoModal = () => {
    resetSelectedNfs();
    setDataPrevista(cte.dprev_entrega || "");
    setPrazoOpen(true);
  };

  const openEventoModal = () => {
    resetSelectedNfs();
    setEventoOpen(true);
  };

  const openComprovanteModal = () => {
    resetSelectedNfs();
    setFiles([]);
    setComprovanteOpen(true);
  };

  const salvarPrazo = async () => {
    if (!dataPrevista) {
      toast.error("Informe a data de entrega prevista");
      return;
    }
    if (selectedNfs.length === 0) {
      toast.error("Selecione pelo menos uma nota fiscal");
      return;
    }
    setSavingPrazo(true);
    try {
      await setPrazoCte({
        numero_cte: cte.cte_numero,
        numeros_notas: selectedNfs,
        data_entrega_prevista: dataPrevista,
      });
      toast.success("Data de entrega prevista salva para as notas selecionadas");
      setPrazoOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar prazo");
    } finally {
      setSavingPrazo(false);
    }
  };

  const salvarEvento = async () => {
    if (!descricao.trim()) {
      toast.error("Informe a descrição da etapa");
      return;
    }
    if (!dataEvento) {
      toast.error("Informe a data e hora do evento");
      return;
    }
    if (selectedNfs.length === 0) {
      toast.error("Selecione pelo menos uma nota fiscal");
      return;
    }
    setSavingEvento(true);
    try {
      await addEventoManualCte({
        numero_cte: cte.cte_numero,
        numeros_notas: selectedNfs,
        categoria,
        data_ocorrencia: dataEvento,
        descricao_ocorrencia: descricao.trim(),
        codigo_ocorrencia: codigo.trim() || undefined,
      });
      toast.success("Etapa manual adicionada às timelines das notas");
      setEventoOpen(false);
      setDescricao("");
      setCodigo("");
      setDataEvento("");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao adicionar etapa");
    } finally {
      setSavingEvento(false);
    }
  };

  const salvarComprovante = async () => {
    if (files.length === 0) {
      toast.error("Selecione pelo menos um arquivo");
      return;
    }
    if (selectedNfs.length === 0) {
      toast.error("Selecione pelo menos uma nota fiscal");
      return;
    }
    setSavingComprovante(true);
    try {
      await uploadComprovantesCte(cte.cte_numero, selectedNfs, files);
      toast.success("Comprovante anexado com sucesso!");
      setComprovanteOpen(false);
      setFiles([]);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar comprovante");
    } finally {
      setSavingComprovante(false);
    }
  };

  // Renderizador da seleção de notas
  const renderNfsChecklist = () => (
    <div className="border border-[#E4E4E7] rounded-md p-3 max-h-40 overflow-y-auto bg-white space-y-2">
      <div className="flex items-center justify-between border-b border-[#F4F4F5] pb-2 mb-2">
        <span className="text-[10px] font-bold text-[#52525B] uppercase tracking-[0.1em]">Notas Fiscais Destinatárias</span>
        <button
          type="button"
          onClick={handleSelectAllNfs}
          className="text-xs text-[#09090B] font-medium hover:underline"
        >
          {selectedNfs.length === nfsDisponiveis.length ? "Desmarcar Todas" : "Selecionar Todas"}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {nfsDisponiveis.map((nf) => (
          <label key={nf} className="flex items-center gap-2 text-xs font-mono-data text-[#09090B] cursor-pointer">
            <input
              type="checkbox"
              checked={selectedNfs.includes(nf)}
              onChange={() => handleToggleNf(nf)}
              className="rounded border-[#E4E4E7] text-[#09090B] focus:ring-[#09090B]"
            />
            NF {nf}
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid={`cte-actions-trigger-${idx}`}
            onClick={(e) => e.stopPropagation()}
            className="p-1.5 rounded-sm text-[#52525B] hover:text-[#09090B] hover:bg-[#FAFAFA] transition-colors"
            aria-label="Ações do CT-e"
          >
            <MoreVertical size={15} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-56"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.15em] text-[#52525B]">
            Ações — CT-e {cte.cte_numero}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid={`cte-action-prazo-${idx}`}
            onClick={openPrazoModal}
            className="cursor-pointer gap-2"
          >
            <CalendarClock size={14} />
            Definir data prevista
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid={`cte-action-evento-${idx}`}
            onClick={openEventoModal}
            className="cursor-pointer gap-2"
          >
            <PlusCircle size={14} />
            Adicionar etapa manual
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid={`cte-action-comprovante-${idx}`}
            onClick={openComprovanteModal}
            className="cursor-pointer gap-2"
          >
            <Paperclip size={14} />
            Anexar comprovante
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid={`cte-action-rastreio-${idx}`}
            onClick={obterLinkRastreio}
            disabled={generatingLink}
            className="cursor-pointer gap-2"
          >
            <Share2 size={14} />
            {generatingLink ? "Gerando link..." : "Gerar link de rastreio"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Dialog: Link de Rastreio Público */}
      <Dialog open={rastreioOpen} onOpenChange={setRastreioOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Link de Rastreamento Público</DialogTitle>
            <DialogDescription>
              Envie este link permanente para seu cliente acompanhar o status de entrega do CT-e {cte.cte_numero} de forma 100% segura e em tempo real.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={rastreioLink}
                className="flex-1 px-3 py-2 text-xs border border-[#E4E4E7] rounded-md bg-[#FAFAFA] text-[#09090B] font-mono"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(rastreioLink);
                  toast.success("Link de rastreamento copiado para a área de transferência!");
                }}
                className="px-3 py-2 text-xs font-semibold bg-[#09090B] text-white hover:bg-zinc-800 rounded-md transition-colors whitespace-nowrap"
              >
                Copiar
              </button>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setRastreioOpen(false)}
              className="px-3 py-2 text-xs border border-[#E4E4E7] text-[#09090B] hover:bg-[#FAFAFA] rounded-md transition-colors"
            >
              Fechar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Definir prazo */}
      <Dialog open={prazoOpen} onOpenChange={setPrazoOpen}>
        <DialogContent data-testid="cte-prazo-dialog" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Data de entrega prevista</DialogTitle>
            <DialogDescription>
              CT-e {cte.cte_numero}. Define o prazo de entrega nas notas selecionadas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className={labelClass}>Data prevista</label>
              <input
                data-testid="cte-prazo-input-data"
                type="date"
                value={dataPrevista}
                onChange={(e) => setDataPrevista(e.target.value)}
                className={inputClass}
              />
            </div>
            {renderNfsChecklist()}
          </div>
          <DialogFooter>
            <button
              onClick={() => setPrazoOpen(false)}
              className="px-4 py-2 text-sm font-medium text-[#52525B] hover:text-[#09090B]"
            >
              Cancelar
            </button>
            <button
              data-testid="cte-prazo-save-btn"
              disabled={savingPrazo}
              onClick={salvarPrazo}
              className="bg-[#09090B] text-white hover:bg-[#27272A] px-4 py-2 text-sm font-medium rounded-md disabled:opacity-50 transition-colors"
            >
              {savingPrazo ? "Salvando..." : "Salvar Prazo"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Evento Manual */}
      <Dialog open={eventoOpen} onOpenChange={setEventoOpen}>
        <DialogContent data-testid="cte-evento-dialog" className="max-w-md" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Adicionar etapa manual</DialogTitle>
            <DialogDescription>
              CT-e {cte.cte_numero}. Insere um evento na timeline das notas selecionadas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Estágio</label>
                <select
                  value={categoria}
                  onChange={(e) => setCategoria(e.target.value)}
                  className={inputClass}
                >
                  {CATEGORIAS.map((cat) => (
                    <option key={cat.key} value={cat.key}>
                      {cat.label.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Código da ocorrência</label>
                <input
                  type="text"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  placeholder="Ex: 80"
                  className={inputClass}
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Data e Hora do Evento</label>
              <input
                type="datetime-local"
                value={dataEvento}
                onChange={(e) => setDataEvento(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Descrição da ocorrência</label>
              <textarea
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Ex: Entrega realizada com sucesso"
                className={`${inputClass} h-20 resize-none`}
              />
            </div>
            {renderNfsChecklist()}
          </div>
          <DialogFooter>
            <button
              onClick={() => setEventoOpen(false)}
              className="px-4 py-2 text-sm font-medium text-[#52525B] hover:text-[#09090B]"
            >
              Cancelar
            </button>
            <button
              data-testid="cte-evento-save-btn"
              disabled={savingEvento}
              onClick={salvarEvento}
              className="bg-[#09090B] text-white hover:bg-[#27272A] px-4 py-2 text-sm font-medium rounded-md disabled:opacity-50 transition-colors"
            >
              {savingEvento ? "Adicionando..." : "Adicionar Etapa"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Anexar Comprovante */}
      <Dialog open={comprovanteOpen} onOpenChange={setComprovanteOpen}>
        <DialogContent data-testid="cte-comprovante-dialog" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Anexar comprovante</DialogTitle>
            <DialogDescription>
              CT-e {cte.cte_numero}. O arquivo será vinculado ao CT-e e às notas selecionadas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className={labelClass}>Selecionar arquivo(s)</label>
              <input
                type="file"
                multiple
                onChange={(e) => setFiles(e.target.files)}
                className={inputClass}
              />
            </div>
            {renderNfsChecklist()}
          </div>
          <DialogFooter>
            <button
              onClick={() => setComprovanteOpen(false)}
              className="px-4 py-2 text-sm font-medium text-[#52525B] hover:text-[#09090B]"
            >
              Cancelar
            </button>
            <button
              data-testid="cte-comprovante-save-btn"
              disabled={savingComprovante}
              onClick={salvarComprovante}
              className="bg-[#09090B] text-white hover:bg-[#27272A] px-4 py-2 text-sm font-medium rounded-md disabled:opacity-50 transition-colors"
            >
              {savingComprovante ? "Enviando..." : "Anexar Arquivo"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
